import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import { Button, Stack, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import TextSearchField from '../common/TextSearchField'
import DateRangePicker from '../common/DateRangePicker'
import { RouteConstants } from '../../navigation/navigation-types'
import { OrderState, PaymentState } from '../../services/api/sharedTypes/Common'
import { EventBriefDto } from '../../services/api/sharedTypes/Event'
import EnhancedTableHead from '../table/EnhancedTableHead'
import OrderStateTag from '../common/OrderStateTag'
import PaymentStateTag from '../common/PaymentStateTag'
import CustomTableCell from '../common/CustomTableCell'
import { convertToSearchString, getNumberRoundedInFormattedString } from '../../utils/global-utils'
import PaymentStateFilter from '../common/PaymentStateFilter'
import OrderStateFilter from '../common/OrderStateFilter'
import LinkWrapper from '../common/LinkWrapper'
import { getEventTableRowColor } from '../../utils/event-utils'
import TableButtonOpen from '../table/TableButtonOpen'

const headCells: readonly HeadCell[] = [
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Název',
    },
    {
        id: 'startsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum od',
    },
    {
        id: 'endsAt',
        numeric: false,
        disablePadding: false,
        label: 'Datum do',
    },
    {
        id: 'orderState',
        numeric: false,
        disablePadding: false,
        label: 'Stav objednávky',
        centered: true,

    },
    {
        id: 'paymentState',
        numeric: false,
        disablePadding: false,
        label: 'Stav platby',
        centered: true,

    },
    {
        id: 'profit',
        numeric: false,
        disablePadding: false,
        label: 'Čistý zisk',
        align: 'right',
    },
    {
        id: 'invoiced',
        numeric: false,
        disablePadding: false,
        label: 'Vyfakturováno',
        align: 'right',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface EventTableProps {
    events: EventBriefDto[]
}

const EventTable: React.FC<EventTableProps> = ({ events }: EventTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('startsAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [dateFrom, setDateFrom] = React.useState<Date | null>(null)
    const [dateTo, setDateTo] = React.useState<Date | null>(null)
    const [selectedOrderState, setSelectedOrderState] = React.useState<OrderState[]>([])
    const [selectedPaymentState, setSelectedPaymentState] = React.useState<PaymentState[]>([])

    const [searchedValue, setSearchedValue] = React.useState<string>('')

    const theme = useTheme()

    const eventsForTable = events
        ?.filter((event) => {
            if (searchedValue) {
                return convertToSearchString(event.name).includes(convertToSearchString(searchedValue))
            }
            return true
        })
        .filter((event) => {
            return selectedOrderState.length === 0 || selectedOrderState.includes(event.orderState)
        })
        .filter((event) => {
            return (
                selectedPaymentState.length === 0 ||
                (event.paymentState === PaymentState.PaidWithoutInvoice
                    ? selectedPaymentState.includes(PaymentState.Paid)
                    : selectedPaymentState.includes(event.paymentState))
            )
        })
        .filter((event) => {
            const date = new Date(event.startsAt)
            if (dateFrom && date < dateFrom) {
                return false
            }
            if (dateTo && date > dateTo) {
                return false
            }
            return true
        })

    const profit = eventsForTable.reduce((acc, event) => acc + event.profit, 0)
    const invoiced = eventsForTable.reduce((acc, event) => acc + (event.invoiced ?? 0), 0)

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Stack justifyContent='space-between' direction='row'>
                <Typography variant='h6' color='primary'>
                    Události
                </Typography>
                <Stack direction='row' spacing={3}>
                    <LinkWrapper route={RouteConstants.addEvent}>
                        <Button variant='contained'>+ PŘIDAT UDÁLOST</Button>
                    </LinkWrapper>
                </Stack>
            </Stack>

            {eventsForTable.length === 0 && (
                <Typography variant='body1'>
                    Klient není přiřazen k žádné události.
                </Typography>
            )}

            {eventsForTable.length > 0 && (
                <>
                    <Stack pb={3} direction='row' spacing={3} overflow='auto'>
                        <OrderStateFilter orderState={selectedOrderState} onChange={setSelectedOrderState} />

                        <PaymentStateFilter onChange={setSelectedPaymentState} paymentState={selectedPaymentState} />

                        <DateRangePicker
                            labelFrom='Datum od'
                            labelTo='Datum do'
                            onSetDateFrom={setDateFrom}
                            onSetDateTo={setDateTo}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                        />

                        <TextSearchField value={searchedValue} onChange={(e) => setSearchedValue(e.target.value)} />
                    </Stack>
                    <Typography variant='body2' style={{ marginBottom: 3 }}>
                        {eventsForTable.length} záznamů
                    </Typography>
                    <Box style={{ width: '100%' }}>
                        <TableContainer>
                            <Table>
                                <EnhancedTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    headCells={headCells}
                                    onRequestSort={handleRequestSort}
                                    rowCount={eventsForTable.length}
                                />
                                <TableBody>
                                    {stableSort(
                                        eventsForTable.map((row) => {
                                            return {
                                                ...row,
                                                startsAt: new Date(row.startsAt).getTime(),
                                                endsAt: new Date(row.endsAt).getTime(),
                                                address: row.address?.street + ' ' + row.address?.city,
                                                createdAt: new Date(row.createdAt).toLocaleDateString(),
                                                lastModifiedAt: new Date(row.lastModifiedAt).toLocaleDateString(),
                                                orderStateChanges: '',
                                                paymentStateChanges: '',
                                                isEverythingReady: row.isEverythingReady ? 'Ano' : 'Ne',
                                                contactPhones: row.contactPhones?.join(', '),
                                                hasContactWithEmail: row.hasContactWithEmail ? 'Ano' : 'Ne',
                                                emailMessagesLogs: '',
                                                contacts: '',
                                            }
                                        }),
                                        getComparator(order, orderBy)
                                    )
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index: number) => {
                                            return (
                                                <TableRow
                                                    hover
                                                    tabIndex={-1}
                                                    key={index}
                                                    style={{
                                                        backgroundColor: getEventTableRowColor(
                                                            row.paymentState,
                                                            theme,
                                                            row.orderState,
                                                        ),
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} isText>
                                                        {row.name}
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} isText>
                                                        {new Date(row.startsAt).toLocaleDateString('cs')}
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} isText>
                                                        {new Date(row.endsAt).toLocaleDateString('cs')}
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} center>
                                                        <OrderStateTag orderState={row.orderState} />
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} center>
                                                        <PaymentStateTag paymentState={row.paymentState} />
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} align='right'>
                                                        {getNumberRoundedInFormattedString(row.profit)}
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} align='right'>
                                                        {getNumberRoundedInFormattedString(row.invoiced ?? 0)}
                                                    </CustomTableCell>
                                                    <CustomTableCell route={RouteConstants.events + '/' + row.id} align='right'>
                                                        <TableButtonOpen route={RouteConstants.events + '/' + row.id} />
                                                    </CustomTableCell>
                                                </TableRow>
                                            )
                                        })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Stack spacing={theme.spacing(2)} direction='row' mt={3} ml={3}>
                            <Typography variant='body2' fontWeight='bold'>
                                Čistý zisk:
                            </Typography>
                            <Typography variant='body2'>{getNumberRoundedInFormattedString(profit)}</Typography>
                            <Typography variant='body2' fontWeight='bold'>
                                Celkem vyfakturováno:
                            </Typography>
                            <Typography variant='body2'>{getNumberRoundedInFormattedString(invoiced)}</Typography>
                        </Stack>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50]}
                            component='div'
                            count={events.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Box>
                </>
            )}
        </>
    )
}

export default EventTable
